// SheetBeacons.tsx — page: live beacon tree + ping history.
//
// Route it next to Sheet Imports, e.g.
//   <Route path="/leads/sheet-beacons" element={<SheetBeacons />} />

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { G, C } from '../../components/GlobalComponents/uiTokens';
import BeaconTree from './parts/BeaconTree';
import BeaconHistory from './parts/BeaconHistory';
import BeaconFormModal from './parts/BeaconFormModal';
import BeaconDeleteModal from './parts/BeaconDeleteModal';
import { BeaconSummary, SheetBeacon, apiError, beaconApi, beaconName } from './parts/beaconTypes';

/** Where the "Sync now" button sends the user. Adjust to your Sheet Imports route. */
const SHEET_IMPORTS_PATH = '/leads/sheet-imports';

const POLL_MS = 15000; // normal refresh
const POLL_FAST_MS = 3000; // while any beacon is checking

const SheetBeacons: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  const [beacons, setBeacons] = useState<SheetBeacon[]>([]);
  const [summary, setSummary] = useState<BeaconSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Clock (corrected for server skew) — drives countdowns without refetching
  const skew = useRef(0);
  const [now, setNow] = useState(Date.now());

  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [historyBeaconId, setHistoryBeaconId] = useState('');
  const [historyKey, setHistoryKey] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SheetBeacon | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SheetBeacon | null>(null);
  const [deleting, setDeleting] = useState(false);

  // unseen counts from the previous fetch → spot new arrivals between polls
  const prevUnseen = useRef<Map<string, number> | null>(null);

  const setBusy = (id: string, on: boolean) =>
    setBusyIds((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });

  const replaceBeacon = (b: SheetBeacon) => setBeacons((list) => list.map((x) => (x.id === b.id ? b : x)));

  const recomputeSummary = (list: SheetBeacon[]) =>
    setSummary((s) => ({
      total: list.length,
      active: list.filter((b) => b.is_active).length,
      errored: list.filter((b) => b.state === 'error').length,
      unseen_new_rows: list.reduce((a, b) => a + b.unseen_new_rows, 0),
      last_ping_at: s?.last_ping_at ?? null,
    }));

  // ── Fetch ────────────────────────────────────────────────────────────────
  // Background refreshes never overlap: coming back to the tab fires the
  // "visible" refresh and the timer together, and two requests with an
  // expired token mean two 401s and a token refresh race.
  const inFlight = useRef(false);
  const fetchBeacons = useCallback(async (silent = false) => {
    if (silent && inFlight.current) return;
    inFlight.current = true;
    try {
      if (!silent) setLoading(true);
      const res = await beaconApi.list();
      const list = res.data ?? [];
      if (res.server_time) skew.current = new Date(res.server_time).getTime() - Date.now();

      // Toast for new arrivals since the last poll (not on first load)
      const prev = prevUnseen.current;
      if (prev) {
        list.forEach((b) => {
          const before = prev.get(b.id) ?? 0;
          if (b.unseen_new_rows > before) {
            const n = b.unseen_new_rows - before;
            toast.current?.show({
              severity: 'success',
              summary: `+${n} new lead${n === 1 ? '' : 's'}`,
              detail: `Arrived in ${beaconName(b)}`,
              life: 6000,
            });
          }
        });
      }
      prevUnseen.current = new Map(list.map((b) => [b.id, b.unseen_new_rows]));

      setBeacons(list);
      setSummary(res.summary ?? null);
      setError(null);
    } catch (e) {
      if (!silent) setError(apiError(e, 'Failed to load beacons'));
    } finally {
      inFlight.current = false;
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBeacons();
  }, [fetchBeacons]);

  // Poll — faster while something is checking, paused while the tab is hidden
  const anyChecking = beacons.some((b) => b.state === 'checking');
  useEffect(() => {
    const t = setInterval(() => {
      if (!document.hidden) fetchBeacons(true);
    }, anyChecking ? POLL_FAST_MS : POLL_MS);
    const onVisible = () => {
      if (!document.hidden) fetchBeacons(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [anyChecking, fetchBeacons]);

  // 1s clock for countdowns
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now() + skew.current), 1000);
    return () => clearInterval(t);
  }, []);

  // ── Actions ──────────────────────────────────────────────────────────────
  const handlePing = async (b: SheetBeacon) => {
    setBusy(b.id, true);
    replaceBeacon({ ...b, state: 'checking' }); // instant spinner + flowing dots
    try {
      const res = await beaconApi.ping(b.id);
      replaceBeacon(res.data);
      prevUnseen.current?.set(b.id, res.data.unseen_new_rows); // this toast is shown here, not by the poll
      const p = res.ping;
      if (res.check_error) {
        toast.current?.show({ severity: 'error', summary: 'Check failed', detail: res.check_error, life: 5000 });
      } else if (p && p.new_rows > 0) {
        toast.current?.show({ severity: 'success', summary: `+${p.new_rows} new lead${p.new_rows === 1 ? '' : 's'}`, detail: beaconName(b), life: 5000 });
      } else {
        toast.current?.show({ severity: 'info', summary: 'No new rows', detail: `${beaconName(b)} · ${p?.total_rows ?? 0} rows`, life: 2500 });
      }
      setHistoryKey((k) => k + 1);
    } catch (e) {
      replaceBeacon(b);
      toast.current?.show({ severity: 'warn', summary: 'Could not ping', detail: apiError(e, 'Try again'), life: 4000 });
    } finally {
      setBusy(b.id, false);
    }
  };

  const handleToggle = async (b: SheetBeacon) => {
    setBusy(b.id, true);
    try {
      const res = await beaconApi.update(b.id, { is_active: !b.is_active });
      replaceBeacon(res.data);
      toast.current?.show({ severity: 'success', summary: res.data.is_active ? 'Beacon resumed' : 'Beacon paused', detail: beaconName(b), life: 2500 });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Update failed', detail: apiError(e, 'Try again'), life: 4000 });
    } finally {
      setBusy(b.id, false);
    }
  };

  const handleSeen = async (b: SheetBeacon) => {
    setBusy(b.id, true);
    try {
      const res = await beaconApi.seen(b.id);
      prevUnseen.current?.set(b.id, 0);
      setBeacons((list) => {
        const next = list.map((x) => (x.id === b.id ? res.data : x));
        recomputeSummary(next);
        return next;
      });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Failed', detail: apiError(e, 'Try again'), life: 4000 });
    } finally {
      setBusy(b.id, false);
    }
  };

  const handleSaved = (b: SheetBeacon, warning?: string) => {
    const isNew = !beacons.some((x) => x.id === b.id);
    prevUnseen.current?.set(b.id, b.unseen_new_rows);
    setBeacons((list) => {
      const next = isNew ? [...list, b] : list.map((x) => (x.id === b.id ? b : x));
      recomputeSummary(next);
      return next;
    });
    setHistoryKey((k) => k + 1);
    if (warning) {
      toast.current?.show({ severity: 'warn', summary: 'Beacon added, first check failed', detail: warning, life: 7000 });
    } else {
      toast.current?.show({
        severity: 'success',
        summary: isNew ? 'Beacon is live' : 'Beacon updated',
        detail: isNew
          ? `${beaconName(b)} · ${b.last_row_count} rows${(b.pending_new_rows ?? 0) > 0 ? ` · ${b.pending_new_rows} not in Leads yet` : ''}`
          : beaconName(b),
        life: 3000,
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const b = deleteTarget;
    setDeleting(true);
    try {
      await beaconApi.remove(b.id);
      prevUnseen.current?.delete(b.id);
      setBeacons((list) => {
        const next = list.filter((x) => x.id !== b.id);
        recomputeSummary(next);
        return next;
      });
      if (historyBeaconId === b.id) setHistoryBeaconId('');
      setHistoryKey((k) => k + 1);
      setDeleteTarget(null);
      toast.current?.show({ severity: 'success', summary: 'Beacon deleted', life: 2500 });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Delete failed', detail: apiError(e, 'Try again'), life: 4000 });
    } finally {
      setDeleting(false);
    }
  };

  const openHistoryFor = (b: SheetBeacon) => {
    setHistoryBeaconId(b.id);
    historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        {/* Header */}
        <div style={{ background: G.shellBg, border: `1px solid ${G.shellBorder}`, borderRadius: '13px', padding: '4.6px' }}>
          <div
            style={{
              background: C.white,
              border: `1px solid ${G.line}`,
              borderRadius: '9.2px',
              padding: '12px 16.6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '9px',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', color: C.sub }}>Leads · Google Sheets</div>
              <h1 style={{ margin: '3.7px 0 0', fontSize: '1.01rem', fontWeight: 700, color: '#111827', letterSpacing: '-0.2px' }}>Sheet Beacons</h1>
            </div>
            <button
              type="button"
              onClick={() => navigate(SHEET_IMPORTS_PATH)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4.6px',
                padding: '6px 12px',
                background: G.shellBg,
                border: `1px solid ${G.line}`,
                borderRadius: '7.4px',
                color: C.teal,
                fontSize: '0.7rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <ArrowLeft size={12} /> Sheet Sync
            </button>
          </div>
        </div>

        {error && <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.79rem' }} />}

        <BeaconTree
          beacons={beacons}
          summary={summary}
          now={now}
          loading={loading}
          busyIds={busyIds}
          onAdd={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          onPing={handlePing}
          onToggle={handleToggle}
          onEdit={(b) => {
            setEditing(b);
            setFormOpen(true);
          }}
          onDelete={setDeleteTarget}
          onSeen={handleSeen}
          onHistory={openHistoryFor}
          onSync={(b) => navigate(SHEET_IMPORTS_PATH, { state: { sheetUrl: b.sheet_url } })}
        />

        <div ref={historyRef} style={{ scrollMarginTop: '12px' }}>
          <BeaconHistory
            beacons={beacons}
            beaconId={historyBeaconId}
            onBeaconIdChange={setHistoryBeaconId}
            refreshKey={historyKey}
            now={now}
          />
        </div>
      </div>

      <BeaconFormModal visible={formOpen} beacon={editing} onHide={() => setFormOpen(false)} onSaved={handleSaved} />

      <BeaconDeleteModal
        visible={!!deleteTarget}
        loading={deleting}
        name={deleteTarget ? beaconName(deleteTarget) : undefined}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default SheetBeacons;
